<template>
  <div class="adv-row" :style="[isAdvanceSetting ? { background: '#323232' } : false]" @click="onEditTag">
    <div class="adv-left">
      <div class="adv-heading" :style="styleText">{{ item.name }}</div>
      <div class="adv-unit-text">{{ unitText }}</div>
    </div>
    <div class="adv-right-icon">
      <Teleport to="#tooltips">
        <Tooltip :pointer="true" :width="'auto'" />
      </Teleport>
      <div v-popover:tooltip.top="'Click to edit'" class="wrap-edit-icon" @click="onEditTag">
        <div class="edit-icon">
          <i class="icon icon-t-blog edit-icon" />
        </div>
      </div>
    </div>
    <Teleport v-if="isShowAdvanceSetting" to="#text-advanced-setting">
      <TextAdvancedSetting
        v-if="isShowAdvanceSetting"
        :item="text"
        :index="index"
        :is-advance-setting="isAdvanceSetting"
        @click-away="clickAway"
        @on-change-text-item="onChangTextItem"
        @on-close-advanced-setting="closeAdvancedSetting" />
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import TextAdvancedSetting from './/TextAdvancedSetting.vue';
import type { TextSetting } from '@/types/global-style';

export default defineComponent({
  name: 'AdvanceTextItemSetting',
  components: {
    TextAdvancedSetting,
  },
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      default: () => 0,
    },
    advancedSetting: {
      type: Number,
      default: () => 0,
    },
    generalSettingText: {
      type: Object,
      default: () => {},
    },
    texts: {
      type: Array,
      default: () => [],
    },
    isShowAdvanceSetting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clickAway', 'changeAdvancedItem', 'onTagClose', 'onTagEdit'],
  computed: {
    text() {
      return this.item;
    },
    unitText() {
      return `${this.displayFontSizeTag} / ${this.baseFontSize}`;
    },
    displayFontSizeTag() {
      if (this.parseFontSize.unit == 'rem') {
        if (!this.item?.value?.fontSize) return '0rem';
        if (isNaN(parseFloat(this.item?.value?.fontSize))) return '0rem';
        return this.item?.value?.screens?.lg?.fontSize || this.item?.value?.fontSize;
      }
      return parseFloat((this.fontSizeUnit / this.baseSizeUnit).toFixed(3)) + 'rem';
    },
    parseFontSize() {
      return this.getValueWithDataUnit(this.item?.value?.screens?.lg?.fontSize ?? this.item?.value?.fontSize);
    },
    baseFontSize() {
      if (this.parseFontSize.unit == 'rem') {
        return parseFloat((this.fontSizeUnit * this.baseSizeUnit).toFixed(2)) + 'px';
      }
      return this.item?.value?.screens?.lg?.fontSize ?? this.item?.value?.fontSize;
    },
    fontSizeUnit() {
      if (!this.item?.value?.fontSize) return '';
      if (isNaN(parseFloat(this.item?.value?.fontSize))) return 0;
      if (this.parseFontSize.unit == 'rem') {
        return parseFloat(this.parseFontSize.value.toFixed(3));
      }

      return parseFloat(this.parseFontSize.value.toFixed(2));
    },
    baseSizeUnit() {
      if (!this.item?.value.baseSize) return '';
      if (isNaN(parseFloat(this.item?.value?.baseSize))) return 0;
      return parseFloat(this.getValueWithDataUnit(this.item?.value.baseSize).value.toFixed(2));
    },
    styleText() {
      const headingFont = this.generalSettingText.headingFont.replace('+', ' ');
      const paragraphFont = this.generalSettingText.paragraphFont.replace('+', ' ');

      let fontFamily = headingFont;
      if (['paragraph-1', 'paragraph-2', 'paragraph-3', 'paragraph-4'].includes(this.item.key)) {
        fontFamily = paragraphFont;
      }

      return {
        fontStyle: this.item?.value?.fontStyle,
        textDecoration: this.item?.value?.decoration,
        fontSize: this.baseFontSize,
        fontWeight: this.item?.value?.fontWeight,
        fontFamily: fontFamily.toLowerCase(),
      };
    },
    isAdvanceSetting() {
      return this.advancedSetting === this.index;
    },
  },
  watch: {
    texts(texts) {
      this.item.value.fontSize = texts[this.index].value?.screens?.lg?.fontSize;
    },
  },
  methods: {
    onChangTextItem(textData: { indexItem: number; text: TextSetting; changeFontSize: boolean }) {
      this.item.value = textData.text;
      this.$emit('changeAdvancedItem', textData);
    },
    closeAdvancedSetting() {
      this.$emit('onTagClose');
    },
    clickAway() {
      this.$emit('clickAway');
    },
    onEditTag() {
      this.$emit('onTagEdit', this.index);
    },
    getValueWithDataUnit(data: any) {
      const result = data.match(/[a-z]+|[^a-z]+/gi);
      return {
        value: parseFloat(result[0]),
        unit: result[1],
      };
    },
    changeAdvanceItem(isChange: boolean) {
      return isChange;
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.adv-row {
  display: flex;
  position: relative;
  margin-bottom: 16px;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  min-height: 35px;

  .adv-left {
    width: 240px;
    padding: 4px 0 4px 4px;

    .adv-heading {
      color: map-get($text-dark-mode, 'high-contrast');
      line-height: 1.2em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 240px;
    }

    .adv-unit-text {
      font-weight: 500;
      font-size: 10px;
      line-height: 14px;
      color: map-get($text-dark-mode, 'disabled');
      padding-bottom: 0;
    }
  }

  .adv-right-icon {
    .wrap-edit-icon {
      display: flex;
      margin-right: 4px;
      width: 32px;
      height: 32px;
      @include flex-align-justify-center;
      border-radius: 3px;
      cursor: pointer;

      &:hover {
        background-color: map-get($hybrid-shade-dark, '300');
      }

      .edit-icon {
        @include flex-align-justify-center;

        font-size: 16px;
        color: #fff;
      }
    }
  }

  &:hover {
    background: #494949;
    border-radius: 3px;

    .\wrap-edit-icon {
      &:hover {
        background-color: map-get($hybrid-shade-dark, '300');
      }
    }
  }
}
</style>
